'use client';

// Đo lường hành vi dùng GMS (client-only) để phân tích & tối ưu UI/UX.
// Bắt tự động: page view (route đã che ID), click (nút/link/tab/chip), chọn value (select/checkbox/radio),
// section view (khối có gắn data-track-section), TỐC ĐỘ TẢI (perf). Có thêm useAnalytics().track() cho
// điểm chạm thủ công.
//
// 📈 perf (RUM — 2026-09-05): đo thời gian mở app THẬT trên máy người dùng. Trước đây khi NV/chủ nhà báo
//    "vào app lâu quá" thì không có số liệu nào để biết là mạng của họ hay code của mình → phải điều tra
//    tay từng lần. Nay mỗi lần mở app ghi 1 nhóm sự kiện perf, xem ở /reports → Hành vi dùng.
//
// NGUYÊN TẮC RIÊNG TƯ: KHÔNG bao giờ ghi nội dung ô nhập text/number/password; nhãn nút/link/value được
// "scrub" (che email, số điện thoại, dãy số dài như CCCD/ID) + cắt ngắn. Route được che segment ID
// (/bookings/GMB-… → /bookings/:id) nên số liệu là về CẤU TRÚC dùng, không phải dữ liệu khách.
//
// CHI PHÍ: sự kiện gom trong buffer, ghi Firestore theo LÔ (1 doc/nhiều sự kiện) mỗi FLUSH_MS hoặc khi
// rời trang/ẩn tab → số ghi rất thấp. Lưu tenants/{tid}/analyticsEvents (rule chung: staff ghi, super-admin đọc chéo).

import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { getFirebaseDb } from './firebase';
import { tenantColClient } from './tenant-db-client';
import { useAuth } from './auth';
import { useTenant } from './tenant-context';
import { logClientError } from './client-error-logger';

// ── Cấu hình ──────────────────────────────────────────────
const FLUSH_MS = 15_000;   // ghi buffer định kỳ
const MAX_BUFFER = 40;     // ghi ngay khi buffer đầy (giới hạn kích thước 1 doc)
const MAX_LABEL = 60;      // độ dài tối đa 1 nhãn

export type AnalyticsEventType = 'page' | 'click' | 'select' | 'section' | 'custom' | 'perf' | 'read';
interface AEvent { t: AnalyticsEventType; p: string; k?: string; v?: string; ts: number }

let modulePush: ((t: AnalyticsEventType, k?: string, v?: string, p?: string) => void) | null = null;

/**
 * Đếm SỐ DOC Firestore mà trang hiện tại đọc — điểm đo còn thiếu để biết trang nào GÂY TẢI.
 *
 * Tab "Hành vi dùng" trước giờ chỉ có LƯỢT XEM, nên muốn biết trang nào tốn tiền đọc phải ngồi
 * đọc code đoán số doc/lượt (đã làm 2026-09-12: /timeline ước 1.625, /conversations ước 967 —
 * nhưng chỉ quy được ~31% hoá đơn Firestore, phần còn lại mù). Có con số này thì hết phải đoán.
 *
 * Ghi cùng hình dạng sự kiện sẵn có (`t:'read'`, `k`=collection, `v`=số doc) → không đẻ collection
 * mới, không thêm chi phí ghi (gom chung batch với page/click đang có).
 */
export function recordDocsRead(collection: string, docs: number): void {
  if (!Number.isFinite(docs) || docs <= 0) return;
  // Cộng dồn vào lần mở hiện tại; đẩy khi rời trang (flushNavDocs). Giữ CẢ tên nguồn.
  const n = Math.round(docs);
  docsThisNav += n;
  if (docsBySrc.size < 40 || docsBySrc.has(collection)) docsBySrc.set(collection, (docsBySrc.get(collection) ?? 0) + n);
  if (BG_SOURCES.has(collection)) bgThisNav.set(collection, (bgThisNav.get(collection) ?? 0) + n);
}

/**
 * Ghi KẾT QUẢ của lớp cache theo dấu mốc (`lib/data-stamp.ts` + `lib/swr-cache.ts`).
 *
 * Không có số này thì không biết cache có ăn không: hoá đơn Firestore giảm hay không giảm đều có thể
 * giải thích bằng "user vào ít hơn", nên phải đo TỈ LỆ TRÚNG chứ đừng suy từ tổng lượt đọc.
 *
 * Dùng lại hình dạng sự kiện `perf` (giống `net`/`navType`: `v` là NHÃN chứ không phải số) → không đẻ
 * collection mới, không thêm lượt ghi (gom chung lô đang có).
 */
export function recordCacheResult(name: string, hit: boolean): void {
  modulePush?.('perf', 'cache', `${hit ? 'hit' : 'miss'}:${name}`);
}

/**
 * Đo ĐỘ TRỄ từ lúc gắn 1 `onSnapshot` tới lúc có callback ĐẦU TIÊN (thành công hoặc lỗi) — khác
 * `usePageReady`/`markPageReady` (đo cả trang, 1 lần/lượt mở). Dùng khi nghi 1 listener CỤ THỂ chậm bất
 * thường mà RUM trang không bắt được (case thật 29/09/2026: `/conversations/[threadId]` đo readiness
 * theo doc `conversation` — tải nhanh — trong khi `messages` là `onSnapshot` riêng có thể treo 20-30s
 * mà không trang nào biết).
 *
 * GHI MỌI LẦN (không chỉ lúc chậm) để sau này tính được p50/p90, không chỉ đếm số lần vượt ngưỡng.
 * `name` đặt cố định (vd 'conv_messages_attach') để tra theo nguồn ở `analyticsEvents` (`k=name`).
 */
export function recordListenerLatency(name: string, ms: number): void {
  modulePush?.('perf', name, String(Math.round(ms)), navPath);
}

// ── Thời gian mở TỪNG TRANG ───────────────────────────────────────────────────────────────────────
// Trước 12/09/2026 chỉ có `appReady` — đo KHỞI ĐỘNG APP, ghi 1 lần, gắn nhãn trang tiếp đất. Chuyển
// từ /timeline sang /bookings/:id KHÔNG sinh phép đo nào, nên 19/20 trang dùng nhiều nhất hoàn toàn
// mù về tốc độ. (Chính vì lẫn 2 thứ này mà một lần xếp hạng "trang chậm" đã ra kết quả sai.)
let navStart = 0;
let navPath = '';
let readyDone = false;
let hiddenDuringNav = false;   // tab bị ẩn trong lúc đo → mẫu vô nghĩa, xem markPageReady
/** Trần cho mẫu RUM khởi động — trên mức này chắc chắn là tab nằm nền, không phải app chậm. */
const BOOT_SAMPLE_MAX_MS = 60_000;
let docsThisNav = 0;                       // tổng doc đọc của LẦN MỞ hiện tại
const docsBySrc = new Map<string, number>();   // …và tách theo NGUỒN (badge/todos/trang…), xoá mỗi nhịp đẩy
// Phần NỀN tích luỹ cho CẢ lần mở — `docsBySrc` bị xoá mỗi 15 giây nên không dùng nó để tính `own` được.
const bgThisNav = new Map<string, number>();

// Tab đang mở của trang NHIỀU TAB (`/reports`, `/finance`, `/settings`). Giữ riêng thay vì nhét thẳng
// vào `navPath` vì `markNavStart` cần biết phần gốc để quyết định giữ hay bỏ — xem ghi chú bên dưới.
let navSubview: string | null = null;

/** Provider gọi mỗi khi đổi route — chốt sổ lần mở trước rồi mở sổ mới. */
function markNavStart(path: string): void {
  flushNavDocs();              // chốt tổng doc của trang vừa rời, TRƯỚC khi đổi navPath
  // GIỮ LẠI tab nếu vẫn là trang gốc đó. Lý do không hiển nhiên: trong CÙNG một lần commit, effect của
  // component CON chạy TRƯỚC effect của provider (cha). Trang nhiều tab khai báo tab ở effect con, nên
  // nếu ở đây ghi đè vô điều kiện thì lần mở đầu tiên luôn mất tab — và mất đúng tab MẶC ĐỊNH, tức tab
  // được dùng nhiều nhất. Cách này không phụ thuộc thứ tự chạy, nên không vỡ khi React đổi lịch effect.
  const keep = navSubview && navPath.startsWith(path + '#') ? navSubview : null;
  navSubview = keep;
  navStart = typeof performance !== 'undefined' ? performance.now() : 0;
  navPath = keep ? `${path}#${keep}` : path;
  readyDone = false;
  hiddenDuringNav = typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

/**
 * Trang nhiều tab: coi mỗi lần ĐỔI TAB như một lần "mở trang" mới về mặt ĐO LƯỜNG.
 *
 * Không có nó thì mọi tab của `/reports` dồn chung một dòng — biết trang đọc 269 doc/lần mở mà không
 * biết tab nào gây ra, tức không dùng được để quyết định sửa chỗ nào.
 */
function markSubviewStart(base: string, subview: string): void {
  const p = `${base}#${subview}`;
  if (navPath === p) return;   // cùng tab, không phải lần mở mới
  flushNavDocs();              // chốt sổ doc của TAB vừa rời
  navSubview = subview;
  navStart = typeof performance !== 'undefined' ? performance.now() : 0;
  navPath = p;
  readyDone = false;
  hiddenDuringNav = typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

/** Đánh dấu tab đã bị ẩn trong lần điều hướng hiện tại → bỏ mẫu thời gian. */
function markNavHidden(): void { hiddenDuringNav = true; }

/**
 * Gộp doc đọc của MỘT lần mở thành 1 mẫu, thay vì mỗi lệnh đọc một mẫu.
 *
 * ⚠️ Bản đầu (12/09) đẩy từng lệnh đọc thành mẫu riêng trong khi cột lại ghi "số doc / LẦN MỞ" —
 * /dashboard có 6 listener nên hiện trung vị 53 doc trong khi tổng thật ~658. Số đúng, nhãn sai,
 * và người đọc không có cách nào biết. Nay cộng dồn theo lần mở rồi mới đẩy 1 mẫu.
 */
/**
 * Nguồn CHẠY NỀN — provider toàn cục chạy lại ở MỌI trang, KHÔNG thuộc trang nào.
 *
 * Vì sao phải tách (bài học 15/09/2026): bảng "doc/lần mở" cộng cả phần nền vào trang đang mở, nên
 * `/knowledge` hiện 409 doc/lần và bị xếp hạng 4 toàn hệ thống — trong khi lệnh đọc THẬT của nó chỉ là
 * `knowledgeBase` 124 doc, cả ngày đọc tổng cộng 858. Gần như toàn bộ con số đó là badge `readBy` chạy
 * nền. Suýt nữa đi tối ưu nhầm một trang hoàn toàn vô tội.
 */
const BG_SOURCES = new Set(['readBy', 'conversations:badge', 'todos-provider', 'todo-counts']);

/**
 * @param final `true` = LẦN MỞ đã kết thúc (đổi route/đổi tab/rời trang) ⇒ chốt `total`/`own`.
 *              `false` = chỉ là nhịp đẩy định kỳ 15 giây ⇒ CHỈ đẩy phần tách theo nguồn.
 *
 * ⚠️ Vì sao phải phân biệt (khiếm khuyết phát hiện 16/09/2026): trước đây nhịp 15 giây cũng đẩy
 * `total`, nên cột "doc / lần mở" thật ra là "doc mỗi cửa sổ 15 giây". Trang có listener chạy liên tục
 * bị chia nhỏ thành hàng chục mẫu bé ⇒ trung vị/p90 thấp giả tạo, đúng những trang nặng nhất lại trông
 * nhẹ nhất. `/conversations/:id` hiện 702 "lần mở" với 11 doc/lần — 702 đó là số cửa sổ 15 giây CÓ đọc,
 * không phải số lần NV mở trang.
 *
 * Phần tách theo NGUỒN vẫn đẩy mỗi 15 giây để không mất dữ liệu nếu tab bị đóng đột ngột; tổng theo
 * nguồn cộng lại vẫn bằng `total` của cả lần mở.
 */
function flushNavDocs(final = true): void {
  if (docsThisNav > 0 && navPath) {
    // Phần tách theo NGUỒN — đẩy ở MỌI nhịp (kể cả định kỳ), rồi xoá sổ để không cộng trùng.
    for (const [src, n] of docsBySrc) modulePush?.('read', src, String(n), navPath);
    if (final) {
      let bg = 0;
      for (const [src, n] of bgThisNav) bg += n;
      //  · k='total' → tổng của LẦN MỞ (giữ nguyên tên để không vỡ số liệu lịch sử).
      //  · k='own'   → phần THUỘC VỀ TRANG = total − nền. ĐÂY mới là số dùng để xếp hạng trang nào nặng.
      modulePush?.('read', 'total', String(docsThisNav), navPath);
      modulePush?.('read', 'own', String(Math.max(0, docsThisNav - bg)), navPath);
    }
  }
  docsBySrc.clear();
  if (final) { docsThisNav = 0; bgThisNav.clear(); }
}

/**
 * TRANG gọi khi dữ liệu chính đã hiện xong — qua hook `usePageReady`, đừng gọi thẳng.
 *
 * `path` = trang GỌI, bắt buộc: nếu chỉ dựa vào `navPath` toàn cục thì mọi lời gọi muộn sẽ bị gán
 * cho trang người dùng đang đứng lúc đó. Bản đầu (12/09) thiếu tham số này và đẻ ra mẫu
 * "/finance 226.771 ms" — trong khi /finance còn chẳng được nối điểm đo.
 *
 * · Chỉ tính LẦN ĐẦU sau mỗi lần đổi route: refetch/realtime bắn lại không được ghi đè, nếu không
 *   p50 bị kéo về 0 và trang chậm trông như trang nhanh.
 * · BỎ mẫu nếu tab từng bị ẩn: `performance.now()` vẫn chạy khi tab nền, còn fetch thì bị trình
 *   duyệt bóp — ra những con số hàng trăm giây không phản ánh gì về tốc độ trang.
 * · Trang không gọi thì đơn giản không có số — KHÔNG bịa giá trị mặc định.
 */
export function markPageReady(path: string): void {
  if (readyDone || !navStart || typeof performance === 'undefined') return;
  if (path !== navPath) return;        // lời gọi muộn của trang đã rời → bỏ, đừng gán nhầm
  readyDone = true;
  if (hiddenDuringNav) return;
  modulePush?.('perf', 'pageReady', String(Math.round(performance.now() - navStart)), navPath);
}

/**
 * Bộ đếm cho `onSnapshot` — KHÁC getDocs, đừng dùng `recordDocsRead(snap.size)` thẳng.
 *
 * Listener realtime bắn lại mỗi khi có thay đổi; cộng `snap.size` mỗi lần sẽ thổi phồng con số lên
 * nhiều lần và làm sai luôn việc quy trách nhiệm hoá đơn. Firestore tính tiền TOÀN BỘ ở lần đầu,
 * các lần sau chỉ tính doc THAY ĐỔI — hàm này đếm đúng như vậy.
 *
 *     const meter = makeSnapshotMeter('bookings');
 *     onSnapshot(q,
 *       snap => { meter(snap); ... },
 *       err => logClientError({ ... }),   // onError BẮT BUỘC — xem CLAUDE.md
 *     );
 */
export function makeSnapshotMeter(collection: string) {
  let first = true;
  return (snap: { size: number; docChanges: () => { length: number } }) => {
    const n = first ? snap.size : snap.docChanges().length;
    first = false;
    recordDocsRead(collection, n);
  };
}

/**
 * CÁCH DÙNG CHUẨN cho mọi trang — 1 dòng, đặt cạnh state loading sẵn có:
 *
 *     usePageReady(!loading);              // trang có cờ loading
 *     usePageReady(data !== null);         // trang dùng onSnapshot, null = chưa về
 *
 * Truyền đúng điều kiện "NỘI DUNG CHÍNH đã hiện", đừng truyền `true` cứng — số đo sẽ thành vô nghĩa.
 */
export function usePageReady(ready: boolean, subview?: string): void {
  const pathname = usePathname();
  const path = subview ? `${maskPath(pathname)}#${subview}` : maskPath(pathname);
  useEffect(() => { if (ready) markPageReady(path); }, [ready, path]);
}

/**
 * Trang NHIỀU TAB gọi MỘT dòng ở component gốc, truyền tab đang mở:
 *
 *     useSubview(tab);
 *
 * Từ đó mọi lượt đọc được quy về `/reports#revenue` thay vì dồn chung `/reports`, và mỗi lần đổi tab
 * mở một phép đo thời gian mới. Muốn có CẢ thời gian mở tab thì component của tab gọi thêm
 * `usePageReady(!loading, 'revenue')` với ĐÚNG tên tab đó.
 */
export function useSubview(subview: string | null | undefined): void {
  const pathname = usePathname();
  useEffect(() => {
    if (!subview) return;
    markSubviewStart(maskPath(pathname), subview);
  }, [pathname, subview]);
}

/** Bọc `getDocs` để tự đếm. Dùng ở các trang nặng thay cho getDocs trần. */
export async function meteredGetDocs<T>(
  collection: string, run: () => Promise<{ size: number } & T>,
): Promise<{ size: number } & T> {
  const snap = await run();
  recordDocsRead(collection, snap.size);
  return snap;
}

// ── Helper: che ID trong route + scrub PII trong nhãn ─────
function maskSeg(s: string): string {
  if (!s) return s;
  if (/^\d+$/.test(s)) return ':id';                 // toàn số
  if (/^[0-9a-f-]{8,}$/i.test(s)) return ':id';      // uuid / hex dài
  if (/^[A-Z]{2,4}-?\d/.test(s)) return ':id';       // mã kiểu GMB-2026…, INV-…
  if (s.length > 24) return ':id';
  return s;
}
export function maskPath(pathname: string): string {
  const parts = (pathname || '/').split('?')[0].split('/').filter(Boolean).map(maskSeg);
  return '/' + parts.join('/');
}
function scrub(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined;
  let s = raw.replace(/\s+/g, ' ').trim();
  if (!s) return undefined;
  s = s.replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, '‹email›');   // email
  s = s.replace(/(?:\+?\d[\s.-]?){8,}\d/g, '‹số›');         // dãy điện thoại
  s = s.replace(/\d{6,}/g, '‹số›');                          // dãy số dài (CCCD/ID)
  if (s.length > MAX_LABEL) s = s.slice(0, MAX_LABEL) + '…';
  return s || undefined;
}
function dayKey(d = new Date()): string {
  // YYYY-MM-DD theo giờ VN
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Ho_Chi_Minh', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
}

// Phần tử "bấm được" gần nhất (đi lên tối đa qua closest).
function actionable(t: EventTarget | null): HTMLElement | null {
  const el = t as Element | null;
  if (!el?.closest) return null;
  return el.closest('[data-track],button,a[href],[role="button"],[role="tab"],[role="menuitem"],[role="switch"],summary') as HTMLElement | null;
}
// Mô tả 1 click → khoá ổn định (không PII).
function describeClick(el: HTMLElement): { k: string; v?: string } | null {
  const dt = el.getAttribute('data-track');
  if (dt) return { k: dt, v: scrub(el.getAttribute('data-track-value')) };
  if (el.tagName === 'A') {
    const href = (el as HTMLAnchorElement).getAttribute('href') || '';
    if (href && !href.startsWith('http') && !href.startsWith('#') && !href.startsWith('mailto') && !href.startsWith('tel'))
      return { k: `link ${maskPath(href)}` };
  }
  const label = scrub(el.getAttribute('aria-label') || el.getAttribute('title') || el.textContent);
  if (!label) return null;
  const role = el.getAttribute('role');
  const kind = el.tagName === 'A' ? 'link' : role === 'tab' ? 'tab' : role === 'switch' ? 'toggle' : 'btn';
  return { k: `${kind} ${label}` };
}
// Khoá cho 1 select/checkbox/radio.
function fieldKey(el: HTMLElement): string {
  const dt = el.getAttribute('data-track');
  if (dt) return dt;
  const lbl = el.getAttribute('aria-label') || el.getAttribute('name') || el.id
    || el.closest('label')?.textContent || el.previousElementSibling?.textContent;
  return `select ${scrub(lbl) ?? '?'}`;
}

// ── Context (track thủ công) ──────────────────────────────
interface AnalyticsCtx { track: (name: string, value?: string) => void }
const Ctx = createContext<AnalyticsCtx | null>(null);
export function useAnalytics(): AnalyticsCtx {
  return useContext(Ctx) ?? { track: () => {} };
}

// ── Provider ──────────────────────────────────────────────
export function AnalyticsProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const { user } = useAuth();
  const { tenantId } = useTenant();
  const pathname = usePathname();

  const buf = useRef<AEvent[]>([]);
  const pathRef = useRef('/');
  const metaRef = useRef({ uid: '', name: '', role: '', tid: '' });
  // Lazy init của useState: chạy ĐÚNG 1 lần cho vòng đời component. Sinh ngẫu nhiên thẳng trong thân
  // render là hàm không thuần (React 19 cấm) — render chạy lại là ra session khác, số liệu vỡ.
  const [sessionId] = useState(() =>
    (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : Math.random().toString(36).slice(2));
  const seenSection = useRef<Set<string>>(new Set());
  const ioRef = useRef<IntersectionObserver | null>(null);

  // Giữ meta/route mới nhất cho listener (tránh stale closure).
  // ⚠️ Ghi ref trong lúc RENDER là không hợp lệ ở React 19 (render phải thuần) — với StrictMode/
  // Compiler, render có thể chạy 2 lần hoặc bị bỏ dở, ref sẽ lệch. Listener chỉ chạy khi có sự kiện
  // (sau render) nên cập nhật trong effect là đủ và đúng.
  useEffect(() => {
    metaRef.current = { uid: user?.uid ?? '', name: user?.displayName ?? '', role: (user?.role as string) ?? '', tid: tenantId };
    pathRef.current = maskPath(pathname);
  });

  const flush = useRef(() => {
    const events = buf.current;
    const { uid, name, role, tid } = metaRef.current;
    if (!events.length || !uid || !tid) return;
    buf.current = [];
    try {
      // setDoc (KHÔNG addDoc): addDoc luôn ghi kèm precondition "exists(false)" (create-only) — nếu
      // mạng chập chờn khiến ACK bị mất, Firestore SDK tự resend đúng mutation đó khi stream nối lại,
      // nhưng lúc đó doc đã được tạo ở lần gửi trước → server trả "Document already exists" dù app chỉ
      // gọi addDoc 1 lần (không phải bug double-flush). setDoc không có precondition → retry chỉ ghi
      // đè cùng dữ liệu, vô hại.
      void setDoc(doc(tenantColClient(getFirebaseDb(), tid, 'analyticsEvents')), {
        uid, displayName: name, role, sessionId: sessionId,
        day: dayKey(), count: events.length, events,
        ua: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 140) : '',
        createdAt: serverTimestamp(),
      }).catch((err) => {
        logClientError({ route: 'lib/analytics.tsx', action: 'flush_analytics_events', message: err instanceof Error ? err.message : String(err) });
      });
    } catch (err) {
      logClientError({ route: 'lib/analytics.tsx', action: 'flush_analytics_events_sync', message: err instanceof Error ? err.message : String(err) });
    }
  });

  // Cho phép gọi từ NGOÀI cây React (helper trong lib, hàm fetch của SWR…) mà không phải luồn hook
  // qua nhiều tầng. Gán trong effect + gỡ khi unmount để không giữ tham chiếu tới provider đã chết.
  useEffect(() => {
    modulePush = (t, k, v, p) => push.current(t, k, v, p);
    return () => { modulePush = null; };
  }, []);

  const push = useRef((t: AnalyticsEventType, k?: string, v?: string, p?: string) => {
    if (!metaRef.current.uid) return;
    const ev: AEvent = { t, p: p ?? pathRef.current, ts: Date.now() };
    if (k) ev.k = k;
    if (v) ev.v = v;
    buf.current.push(ev);
    if (buf.current.length >= MAX_BUFFER) flush.current();
  });

  // ── Tốc độ tải (RUM) — ghi 1 lần mỗi lần mở app ───────────────────────────
  // AnalyticsProvider nằm SAU TenantLoadingGuard trong AppShell → lúc nó mount cũng chính là lúc
  // user nhìn thấy giao diện app đầu tiên. performance.now() ở đây = tổng thời gian từ khi bấm link
  // đến khi app dùng được ("appReady") — con số đúng nhất để so máy/mạng của từng người.
  //
  // 🐛 BỎ MẪU KHI TAB KHÔNG HIỂN THỊ (vá 24/09/2026) — `performance.now()` chạy cả lúc tab bị treo ở
  // nền, nên tab mở sẵn rồi để đó (cmd-click, PWA khôi phục, điện thoại khoá màn) ghi ra những con số
  // vô nghĩa. Đo 5 ngày: **45/590 mẫu > 60 giây**, lớn nhất **8.208.615 ms (2,3 giờ)**, kéo `appReady`
  // p90 từ 4.550 lên **12.394 ms** và p90 của `reload` lên 138.226 ms. Suýt nữa đi tối ưu "boot chậm"
  // dựa trên con số của một cái tab đang ngủ.
  // `markPageReady` đã có đúng phép lọc này (`hiddenDuringNav`) từ trước — khối RUM khởi động thì
  // chưa, nên vá cho khớp: kiểm CẢ lúc đo lẫn sự kiện ẩn xảy ra trước đó.
  useEffect(() => {
    if (typeof performance === 'undefined') return;
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    let hiddenSinceLoad = false;
    const onHide = () => { if (document.visibilityState === 'hidden') hiddenSinceLoad = true; };
    document.addEventListener('visibilitychange', onHide);
    const t = setTimeout(() => {
      if (hiddenSinceLoad || document.visibilityState !== 'visible') return;
      // Ca khó nhất: tab được mở ở NỀN rồi mới bật lên xem. Lúc effect chạy tab ĐANG hiển thị nên hai
      // phép kiểm trên đều sạch, mà `performance.now()` thì đã cộng dồn cả quãng nằm nền. Chrome cho
      // đọc lại dòng thời gian hiển thị (`visibility-state`); Safari chưa có ⇒ chặn thêm bằng trần
      // hợp lý: không lần mở app nào lành mạnh mất quá 60 giây.
      const elapsed = Math.round(performance.now());
      const vis = performance.getEntriesByType('visibility-state') as PerformanceEntry[] | undefined;
      if (vis?.some(e => e.name === 'hidden')) return;
      if (elapsed > BOOT_SAMPLE_MAX_MS) return;
      try {
        const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
        push.current('perf', 'appReady', String(elapsed));
        if (nav) {
          push.current('perf', 'ttfb', String(Math.round(nav.responseStart)));
          push.current('perf', 'domReady', String(Math.round(nav.domContentLoadedEventEnd)));
          push.current('perf', 'navType', nav.type);           // navigate | reload | back_forward
        }
        // Chất lượng mạng do trình duyệt tự ước lượng (Chrome/Android có; Safari iOS không) — để phân biệt
        // "app chậm" với "sóng yếu". KHÔNG phải dữ liệu cá nhân.
        const conn = (navigator as Navigator & { connection?: { effectiveType?: string } }).connection;
        if (conn?.effectiveType) push.current('perf', 'net', conn.effectiveType);
      } catch { /* navigator.connection thiếu ở trình duyệt cũ — window.navigator API, miễn trừ NO-SILENT */ }
    }, 0); // sang tick sau để metaRef.uid/tid chắc chắn đã có (push bỏ qua khi chưa có uid)
    return () => { clearTimeout(t); document.removeEventListener('visibilitychange', onHide); };
  }, []);

  // IntersectionObserver cho section (khối gắn data-track-section) — tạo 1 lần.
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (!e.isIntersecting || e.intersectionRatio < 0.4) continue;
        const name = (e.target as HTMLElement).getAttribute('data-track-section');
        if (!name || seenSection.current.has(name)) continue;
        seenSection.current.add(name);
        push.current('section', name);
      }
    }, { threshold: [0, 0.4] });
    ioRef.current = io;
    return () => { io.disconnect(); ioRef.current = null; };
  }, []);

  // Page view + rescan section mỗi khi đổi route.
  useEffect(() => {
    const mp = maskPath(pathname);
    markNavStart(mp);
    push.current('page', undefined, undefined, mp);
    seenSection.current = new Set();
    const scan = () => {
      const io = ioRef.current;
      if (!io) return;
      io.disconnect();
      document.querySelectorAll('[data-track-section]').forEach(el => io.observe(el));
    };
    const t1 = setTimeout(scan, 300);
    const t2 = setTimeout(scan, 1500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [pathname]);

  // Listener click/change + flush định kỳ/khi rời trang — gắn 1 lần.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const el = actionable(e.target);
      if (!el) return;
      const d = describeClick(el);
      if (d) push.current('click', d.k, d.v);
    };
    const onChange = (e: Event) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      if (t.tagName === 'SELECT') {
        const sel = t as HTMLSelectElement;
        push.current('select', fieldKey(sel), scrub(sel.options[sel.selectedIndex]?.text) ?? scrub(sel.value));
      } else if (t.tagName === 'INPUT') {
        const inp = t as HTMLInputElement;
        // CHỈ checkbox/radio (enum) — KHÔNG ghi ô text/number/password (riêng tư)
        if (inp.type === 'checkbox') push.current('select', fieldKey(inp), inp.checked ? 'on' : 'off');
        else if (inp.type === 'radio') push.current('select', fieldKey(inp), scrub(inp.value) ?? 'on');
      }
    };
    // Tab ẩn GIỮA CHỪNG cũng làm hỏng phép đo thời gian (performance.now() vẫn chạy, fetch bị bóp)
    // → đánh dấu ngay, không chỉ kiểm lúc bắt đầu điều hướng.
    const onHide = () => {
      if (document.visibilityState !== 'hidden') return;
      markNavHidden();
      flushNavDocs();          // chốt sổ doc của trang hiện tại trước khi đẩy buffer đi
      flush.current();
    };
    const onPageHide = () => { flushNavDocs(); flush.current(); };
    document.addEventListener('click', onClick, true);
    document.addEventListener('change', onChange, true);
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', onPageHide);
    // `false` = chưa kết thúc lần mở: chỉ đẩy phần theo nguồn, chưa chốt `total` (xem flushNavDocs).
    const iv = setInterval(() => { flushNavDocs(false); flush.current(); }, FLUSH_MS);
    return () => {
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('change', onChange, true);
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', onPageHide);
      clearInterval(iv);
      flush.current();
    };
  }, []);

  const value = useMemo<AnalyticsCtx>(() => ({ track: (name, v) => push.current('custom', name, v) }), []);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
